/**
 * sl-oll9.13: StreamLainEventRouter
 *
 * Routes custom window events to the appropriate StreamLain services.
 * Extracted from StreamLainSidebarSetup for Single Responsibility.
 *
 * Note: Database fullscreen events are handled directly by FullscreenRegistry
 * to avoid DI scope/timing issues.
 */

import { notify } from '@affine/component';
import { I18n } from '@affine/i18n';
import type { ReadinessTileId } from '@streamlain/automation-safety-contract';
import { Service } from '@toeverything/infra';

import { Workbench } from '../../workbench';
import {
  AutomationService,
  OPEN_ROW_AUTOMATION_HISTORY_EVENT,
  type RowHistoryTarget,
} from './automation';
import { AUTOMATION_SIDEBAR_TAB_ID } from './automation-sidebar-sync';
import { FullscreenRegistry } from './fullscreen-registry';
import {
  PENDING_DECISION_REVIEW_EVENT,
  type PendingDecisionReviewDetail,
} from './sidebar-setup';

export class StreamLainEventRouter extends Service {
  private readonly handleConfigure: (e: Event) => void;
  private readonly handleValidationError: (e: Event) => void;
  private readonly handlePendingDecisionReview: (e: Event) => void;
  private readonly handleElsterFullscreen: (e: Event) => void;
  private readonly handleOpenAutomationSidebar: () => void;
  private readonly handleOpenRowAutomationHistory: (e: Event) => void;

  constructor(
    private readonly automationService: AutomationService,
    private readonly workbench: Workbench,
    private readonly fullscreenRegistry: FullscreenRegistry
  ) {
    super();

    this.handleConfigure = (e: Event) => {
      const { blockId, tile, attention } = (
        e as CustomEvent<{
          blockId: string;
          tile?: ReadinessTileId;
          attention?: boolean;
        }>
      ).detail;
      this.automationService.setActiveBlock(blockId);
      this.automationService.setPendingSettingsTarget({
        blockId,
        tile: tile ?? null,
        attention: !!attention,
      });
    };

    this.handleElsterFullscreen = (e: Event) => {
      const customEvent = e as CustomEvent<{ blockId: string }>;
      this.fullscreenRegistry.open('elster', customEvent.detail.blockId);
    };

    this.handleValidationError = (e: Event) => {
      const customEvent = e as CustomEvent<{
        blockId: string;
        reasonKey: string;
      }>;
      const reasonKey = customEvent.detail.reasonKey;
      const i18nKey = `com.streamlain.streamlain.validation.${reasonKey}`;
      notify.warning({
        title: I18n.t(
          'com.streamlain.streamlain.validation.cannot-activate-trigger'
        ),
        message: I18n.t(i18nKey),
      });
    };

    this.handlePendingDecisionReview = (e: Event) => {
      const detail = (
        e as CustomEvent<PendingDecisionReviewDetail & { _routed?: boolean }>
      ).detail;
      if (detail?._routed) return;
      this.automationService.setActiveBlock(null);
      this.workbench.openSidebar();
      this.workbench.activeView$.value.activeSidebarTab(
        AUTOMATION_SIDEBAR_TAB_ID
      );
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent(PENDING_DECISION_REVIEW_EVENT, {
            detail: { ...detail, _routed: true },
          })
        );
      }, 150);
    };

    this.handleOpenAutomationSidebar = () => {
      this.automationService.setActiveBlock(null);
      this.workbench.openSidebar();
      this.workbench.activeView$.value.activeSidebarTab(
        AUTOMATION_SIDEBAR_TAB_ID
      );
    };

    this.handleOpenRowAutomationHistory = (e: Event) => {
      const { databaseId, rowId } = (e as CustomEvent<RowHistoryTarget>).detail;
      this.automationService.setRowHistoryTarget({ databaseId, rowId });
      this.workbench.openSidebar();
      this.workbench.activeView$.value.activeSidebarTab(
        AUTOMATION_SIDEBAR_TAB_ID
      );
    };

    window.addEventListener('automation-configure', this.handleConfigure);
    window.addEventListener(
      OPEN_ROW_AUTOMATION_HISTORY_EVENT,
      this.handleOpenRowAutomationHistory
    );
    window.addEventListener(
      'streamlain:open-automation-sidebar',
      this.handleOpenAutomationSidebar
    );
    window.addEventListener(
      'automation-validation-error',
      this.handleValidationError
    );
    window.addEventListener(
      PENDING_DECISION_REVIEW_EVENT,
      this.handlePendingDecisionReview
    );
    window.addEventListener('elster-fullscreen', this.handleElsterFullscreen);
  }

  override dispose(): void {
    window.removeEventListener('automation-configure', this.handleConfigure);
    window.removeEventListener(
      OPEN_ROW_AUTOMATION_HISTORY_EVENT,
      this.handleOpenRowAutomationHistory
    );
    window.removeEventListener(
      'streamlain:open-automation-sidebar',
      this.handleOpenAutomationSidebar
    );
    window.removeEventListener(
      'automation-validation-error',
      this.handleValidationError
    );
    window.removeEventListener(
      PENDING_DECISION_REVIEW_EVENT,
      this.handlePendingDecisionReview
    );
    window.removeEventListener(
      'elster-fullscreen',
      this.handleElsterFullscreen
    );
  }
}
